import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounceValue } from 'usehooks-ts';

import { gridFilterItemToNumberFilterParams } from '~/components/table/helpers';
import { dateToDateString } from '~/components/utils/dateUtils';
import { type GlobalFilterContext, useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { fetchInvoicesFilter } from '~/data/hooks/fetchInvoicesFilter';
import useInvoices, { type UseInvoicesParameters } from '~/data/hooks/useInvoices';
import {
  InvoiceKind,
  InvoiceKindKey,
  invoiceKindValues,
  type InvoiceStatusKey,
  type InvoiceTypeKey,
  type InvoiceWithPortfolio,
  type InvoiceWithTickets,
} from '~/data/schemas/invoice';
import { mergeFilterModel, useGridFilterModel } from '~/hooks/useGridFilterModel';

export const invoiceWithPortfolio = (invoice: InvoiceWithTickets): InvoiceWithPortfolio => {
  return {
    ...invoice,
    portfolio: invoice.property?.portfolio ?? null,
  };
};

/**
 * The items below that come from the global filter bar rather than from anything the reader
 * set on the table. Spending's query reads the bar through the global filter context, so
 * these only describe where the reader is — a view must not store them, and Clear Filters
 * must not take them away. See `ExternalFilterItemIds`.
 */
export const GLOBAL_FILTER_ITEM_IDS = ['region', 'serviceProvider'];

export const getFilterModelFromQueryParams = (searchParams: URLSearchParams): GridFilterModel => {
  const items: GridFilterModel['items'] = [];

  const proposedAmount = searchParams.get('proposed_amount');
  if (proposedAmount) {
    items.push({
      id: 'proposed_amount',
      field: 'proposedAmount',
      operator: '=',
      value: proposedAmount,
    });
  }

  const proposedAmountGreaterThan = searchParams.get('proposed_amount_gt');
  if (proposedAmountGreaterThan) {
    items.push({
      id: 'proposed_amount',
      field: 'proposedAmount',
      operator: '>',
      value: proposedAmountGreaterThan,
    });
  }

  const proposedAmountGreaterThanEqual = searchParams.get('proposed_amount_gte');
  if (proposedAmountGreaterThanEqual) {
    items.push({
      id: 'proposed_amount',
      field: 'proposedAmount',
      operator: '>=',
      value: proposedAmountGreaterThanEqual,
    });
  }

  const proposedAmountLessThan = searchParams.get('proposed_amount_lt');
  if (proposedAmountLessThan) {
    items.push({
      id: 'proposed_amount',
      field: 'proposedAmount',
      operator: '<',
      value: proposedAmountLessThan,
    });
  }

  const proposedAmountLessThanEqual = searchParams.get('proposed_amount_lte');
  if (proposedAmountLessThanEqual) {
    items.push({
      id: 'proposed_amount',
      field: 'proposedAmount',
      operator: '<=',
      value: proposedAmountLessThanEqual,
    });
  }

  const region = searchParams.getAll('region');
  if (region) {
    for (const r of region) {
      items.push({
        id: 'region',
        field: 'region',
        operator: 'contains',
        value: r,
      });
    }
  }

  const number = searchParams.get('number');
  if (number) {
    items.push({
      id: 'number',
      field: 'number',
      operator: 'equals',
      value: number,
    });
  }

  const serviceProviders = searchParams.getAll('service_provider');
  if (serviceProviders) {
    for (const serviceProvider of serviceProviders) {
      items.push({
        id: 'serviceProvider',
        field: 'serviceProvider',
        operator: 'contains',
        value: serviceProvider,
      });
    }
  }

  const type = searchParams.getAll('invoice_type') as InvoiceTypeKey[];
  if (type.length > 0) {
    items.push({
      id: 'type',
      field: 'type',
      operator: 'isAnyOf',
      value: type,
    });
  }

  const status = searchParams.getAll('invoice_status') as InvoiceStatusKey[];
  if (status.length > 0) {
    items.push({
      id: 'status',
      field: 'status',
      value: status,
      operator: 'isAnyOf',
    });
  }

  const rejectionCategory = searchParams.getAll('rejection_category');
  if (rejectionCategory.length > 0) {
    items.push({
      id: 'rejectionCategory',
      field: 'rejectionCategory',
      value: rejectionCategory,
      operator: 'isAnyOf',
    });
  }

  const property = searchParams.get('property');
  if (property) {
    items.push({
      id: 'property',
      field: 'property',
      operator: 'equals',
    });
  }

  const propertyName = searchParams.get('property_name');
  if (propertyName) {
    items.push({
      id: 'property-name',
      field: 'property',
      operator: 'contains',
      value: propertyName,
    });
  }

  const startDateStamped = searchParams.get('date_stamped_start');
  if (startDateStamped) {
    items.push({
      id: 'dateStampedStart',
      field: 'dateStamped',
      operator: 'onOrAfter',
      // Parse date-only strings as local calendar dates (parseISO), not UTC
      // midnight (new Date), so the round-trip through dateToDateString —
      // which formats in local time — preserves the yyyy-MM-dd across timezones.
      value: parseISO(startDateStamped),
    });
  }

  const endDateStamped = searchParams.get('date_stamped_end');
  if (endDateStamped) {
    items.push({
      id: 'dateStampedEnd',
      field: 'dateStamped',
      operator: 'onOrBefore',
      value: parseISO(endDateStamped),
    });
  }

  const startDateUploaded = searchParams.get('date_uploaded_start');
  if (startDateUploaded) {
    items.push({
      id: 'dateUploadedStart',
      field: 'dateUploaded',
      operator: 'onOrAfter',
      value: parseISO(startDateUploaded),
    });
  }

  const endDateUploaded = searchParams.get('date_uploaded_end');
  if (endDateUploaded) {
    items.push({
      id: 'dateUploadedEnd',
      field: 'dateUploaded',
      operator: 'onOrBefore',
      value: parseISO(endDateUploaded),
    });
  }

  return { items };
};

/**
 * Handles the special "action-needed" filter value.
 * - "Action Needed" only: disputeStatus=active AND disputeNeedsAttention=true
 * - "Active" only: disputeStatus=active AND disputeNeedsAttention=false (excludes action-needed)
 * - Both "Action Needed" + "Active": disputeStatus=active (includes all active disputes)
 * - Other statuses: pass through as-is
 */
const getDisputeFilterParams = (
  disputeStatusValue: string[] | undefined,
): { disputeStatus?: string[]; disputeNeedsAttention?: boolean } => {
  if (!disputeStatusValue || disputeStatusValue.length === 0) {
    return {};
  }

  const hasActionNeeded = disputeStatusValue.includes('action-needed');
  const hasActive = disputeStatusValue.includes('active');
  const otherStatuses = disputeStatusValue.filter((s) => s !== 'action-needed' && s !== 'active');

  // Both "Action Needed" and "Active" selected: return all active disputes
  if (hasActionNeeded && hasActive) {
    return {
      disputeStatus: otherStatuses.length > 0 ? ['active', ...otherStatuses] : ['active'],
    };
  }

  // Only "Action Needed" selected: active disputes that need attention
  if (hasActionNeeded) {
    return {
      disputeStatus: otherStatuses.length > 0 ? ['active', ...otherStatuses] : ['active'],
      disputeNeedsAttention: true,
    };
  }

  // Only "Active" selected: active disputes that DON'T need attention
  if (hasActive) {
    return {
      disputeStatus: otherStatuses.length > 0 ? ['active', ...otherStatuses] : ['active'],
      disputeNeedsAttention: false,
    };
  }

  // No action-needed or active, just other statuses
  return { disputeStatus: disputeStatusValue };
};

type BuildFilterParamsOpts = {
  propertyId: string | null;
  page: number;
  pageSize: number;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  currentView: InvoiceKindKey;
  globalFilters: GlobalFilterContext['selectedFilters'];
};

/**
 * The columns `InvoiceFilter.ordering` accepts. Sorting is server-side, and
 * `OrderingFilter` is a `ChoiceFilter`: an unregistered value fails form validation,
 * which `DjangoFilterBackend` (`raise_exception = True`) turns into a 400 for the
 * whole request. Sorts outside this set are dropped rather than sent.
 */
const INVOICE_ORDERING_FIELDS = new Set([
  'number',
  'date',
  'dateStamped',
  'dateUploaded',
  'rejectionCategory',
  'type',
  'kind',
  'status',
  'property',
  'serviceProviderGroup',
  'portfolio',
  'ticketNumber',
  'tickets',
  'relatedInvoices',
  'proposedAmount',
  'reviewedAmount',
  'savings',
  'disputeStatus',
  'projectedSavings',
]);

/** The grid's sort model as the endpoint's `ordering` param, or `undefined` for none. */
export const invoiceOrderingParam = (sortModel: GridSortModel): string | undefined => {
  const ordering = sortModel
    .filter((item) => INVOICE_ORDERING_FIELDS.has(item.field))
    .map((item) => `${item.sort === 'desc' ? '-' : ''}${item.field}`);
  return ordering.length > 0 ? ordering.join(',') : undefined;
};

/**
 * A date filter's value as the query's `yyyy-MM-dd`, or `undefined` for one that holds no
 * date. Takes a string as well as a `Date`, as Compliance's `buildFilterParams` does: a value
 * from a saved view went through JSON, and formatting anything that is not a valid date
 * throws during render, which took the whole page down on every load.
 */
const filterDateParam = (value: unknown): string | undefined => {
  if (typeof value !== 'string' && !(value instanceof Date)) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : dateToDateString(date);
};

/**
 * The half of the invoice query that comes from the grid's own filter model.
 *
 * Every table of invoices filters server-side: the rows are paged by the API, so a
 * filter applied in the browser would narrow the page in hand and leave both the count
 * and the pages after it unfiltered.
 */
export const invoiceParamsFromFilterModel = (
  filterModel: GridFilterModel,
): NonNullable<UseInvoicesParameters> => {
  const dateParam = (field: string, operator: 'onOrAfter' | 'onOrBefore') =>
    filterDateParam(
      filterModel.items.find((item) => item.field === field && item.operator === operator)?.value,
    );

  const rejectionCategory = filterModel.items.find((item) => item.field === 'rejectionCategory');
  const kind = filterModel.items.find((item) => item.field === 'kind')?.value;

  return {
    search: filterModel.quickFilterValues?.map((value) => value).join(' '),
    kind: kind?.length ? kind : undefined,
    number: filterModel.items.find((item) => item.field === 'number')?.value,
    tickets: filterModel.items.find((item) => item.field === 'tickets')?.value,
    relatedInvoices: filterModel.items.find((item) => item.field === 'relatedInvoices')?.value,
    type: filterModel.items.find((item) => item.field === 'type')?.value,
    status: filterModel.items.find((item) => item.field === 'status')?.value,
    ...getDisputeFilterParams(
      filterModel.items.find((item) => item.field === 'disputeStatus')?.value,
    ),
    propertyName: filterModel.items.find((item) => item.field === 'property')?.value,
    proposedAmount: gridFilterItemToNumberFilterParams(
      filterModel.items.filter((item) => item.field === 'proposedAmount'),
    ),
    reviewedAmount: gridFilterItemToNumberFilterParams(
      filterModel.items.filter((item) => item.field === 'reviewedAmount'),
    ),
    savings: gridFilterItemToNumberFilterParams(
      filterModel.items.filter((item) => item.field === 'savings'),
    ),
    startDateStamped: dateParam('dateStamped', 'onOrAfter'),
    endDateStamped: dateParam('dateStamped', 'onOrBefore'),
    startDateUploaded: dateParam('dateUploaded', 'onOrAfter'),
    endDateUploaded: dateParam('dateUploaded', 'onOrBefore'),
    rejectionCategory: rejectionCategory?.value?.length ? rejectionCategory.value : undefined,
  };
};

export const buildFilterParams = ({
  propertyId,
  page,
  pageSize,
  filterModel,
  sortModel,
  currentView,
  globalFilters,
}: BuildFilterParamsOpts): UseInvoicesParameters | undefined => {
  if (pageSize <= 0 || page < 0) {
    return undefined;
  }

  return {
    ...invoiceParamsFromFilterModel(filterModel),
    limit: pageSize.toString(),
    offset: (page * pageSize).toString(),
    ordering: invoiceOrderingParam(sortModel) ?? '-date',
    // The view toggle owns the class here, so the Class column is not filterable
    // and cannot contradict it.
    kind: [currentView],
    portfolio: globalFilters.portfolios,
    serviceProvider: globalFilters.serviceProviders,
    region: globalFilters.regions,
    startDate: dateToDateString(globalFilters.date.start) ?? undefined,
    endDate: dateToDateString(globalFilters.date.end) ?? undefined,
    property: propertyId ?? undefined,
  };
};

export type UseSpendingOptions = {
  propertyId?: string | null;
  paginationModel: GridPaginationModel;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  currentView: InvoiceKindKey;
};

const defaultSortModel: GridSortModel = [{ field: 'date', sort: 'desc' }];
const spendingViewTitle: Record<InvoiceKindKey, string> = {
  [InvoiceKind.Invoice]: 'Invoices',
  [InvoiceKind.Proposal]: 'Proposals',
  [InvoiceKind.Credit]: 'Credits',
};
const isInvoiceKind = (value: string): value is InvoiceKindKey =>
  (invoiceKindValues as readonly string[]).includes(value);
const spendingViewList = [
  { value: InvoiceKind.Invoice, title: 'Invoices' },
  { value: InvoiceKind.Proposal, title: 'Proposals' },
  { value: InvoiceKind.Credit, title: 'Credits' },
];

type UseSpendingTableModelOpts = {
  initialFilterModel?: GridFilterModel;
};
export function useSpendingTableModel({ initialFilterModel }: UseSpendingTableModelOpts = {}) {
  const [searchParams, setURLSearchParams] = useSearchParams();
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  const [spendingView, setSpendingView] = useState<InvoiceKindKey>(() => {
    const fromUrl = searchParams.get('invoice_kind');
    return fromUrl && isInvoiceKind(fromUrl) ? fromUrl : InvoiceKind.Invoice;
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const filterModelFromParams = getFilterModelFromQueryParams(searchParams);

  const { filterModel, setFilterModel, quickFilterProps } = useGridFilterModel({
    initialState: initialFilterModel
      ? mergeFilterModel(initialFilterModel, filterModelFromParams)
      : filterModelFromParams,
    quickFilterPlaceHolder: `Search ${spendingViewTitle[spendingView]}...`,
  });

  /**
   * Selects a class by value. `onViewChange` below is the `ToggleButtonGroup` adapter
   * over this, for a caller driving the choice from something else — the property page's
   * `SectionTabs`.
   */
  const selectSpendingView = useCallback(
    (newView: string) => {
      if (!isInvoiceKind(newView)) return;
      setSpendingView(newView);

      setURLSearchParams((prev) => {
        prev.set('invoice_kind', newView);
        return prev;
      });
    },
    [setURLSearchParams, setSpendingView],
  );

  const onViewChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newView: string) => selectSpendingView(newView),
    [selectSpendingView],
  );

  return {
    sortModel,
    setSortModel,
    spendingView,
    onViewChange,
    selectSpendingView,
    paginationModel,
    setPaginationModel,
    filterModel,
    setFilterModel,
    quickFilterProps,
    spendingViewList,
  };
}

/**
 * How many invoices and how many proposals there are — the numbers beside Invoices and
 * Proposals on the class control.
 *
 * One 1-row read per class rather than the table's own total, which only ever counts
 * the class you are looking at. Scoped by the global filters and nothing else:
 * following the filter model too would make the badge and the grid's footer two
 * numbers for the same thing, and would refetch a pair on every keystroke.
 */
export function useSpendingClassCounts({ propertyId = null }: { propertyId?: string | null } = {}) {
  const { selectedFilters } = useGlobalFilterContext();

  const scope = useMemo(
    () => ({
      limit: '1',
      portfolio: selectedFilters.portfolios,
      serviceProvider: selectedFilters.serviceProviders,
      region: selectedFilters.regions,
      startDate: dateToDateString(selectedFilters.date.start) ?? undefined,
      endDate: dateToDateString(selectedFilters.date.end) ?? undefined,
      property: propertyId ?? undefined,
    }),
    [selectedFilters, propertyId],
  );

  const { metadata: invoiceMetadata, isLoading: invoicesLoading } = useInvoices(
    { ...scope, kind: [InvoiceKind.Invoice] },
    true,
  );
  const { metadata: proposalMetadata, isLoading: proposalsLoading } = useInvoices(
    { ...scope, kind: [InvoiceKind.Proposal] },
    true,
  );
  const { metadata: creditMetadata, isLoading: creditsLoading } = useInvoices(
    { ...scope, kind: [InvoiceKind.Credit] },
    true,
  );

  return {
    invoices: invoicesLoading ? undefined : invoiceMetadata?.numInvoices,
    proposals: proposalsLoading ? undefined : proposalMetadata?.numInvoices,
    credits: creditsLoading ? undefined : creditMetadata?.numInvoices,
  };
}

export function useSpending({
  propertyId = null,
  paginationModel,
  filterModel,
  sortModel,
  currentView,
}: UseSpendingOptions) {
  const { selectedFilters } = useGlobalFilterContext();

  const filterParams = useMemo(
    () =>
      buildFilterParams({
        propertyId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        filterModel,
        sortModel,
        globalFilters: selectedFilters,
        currentView,
      }),
    [propertyId, paginationModel, filterModel, sortModel, selectedFilters, currentView],
  );

  const [filterParamsDebounced] = useDebounceValue(filterParams, 250);

  const { invoices: data, metadata, isLoading } = useInvoices(filterParamsDebounced);

  const onExport = useCallback(async () => {
    // No limit/offset → the endpoint returns every matching invoice (bare array),
    // which fetchInvoicesFilter normalises into `results` (AMP-503).
    const { results } = await fetchInvoicesFilter({
      ...filterParams,
      limit: undefined,
      offset: undefined,
    });
    return results.map((result) => invoiceWithPortfolio(result));
  }, [filterParams]);

  const invoices = data?.map((invoice) => invoiceWithPortfolio(invoice)) ?? [];
  const numInvoices = metadata?.numInvoices ?? 0;

  return {
    invoices,
    numInvoices,
    metadata,
    isLoading,
    onExport,
  };
}
